import { useState, useEffect } from 'react'
import './OnlineStatus.css'

type StatusType = 'online' | 'shutting-off' | 'offline' | 'waking-up'

interface TimeBasedStatus {
  type: StatusType
  text: string
}

const wakingUpMessages = [
  'Aan het opstarten',
  'Koffie aan het zetten',
  'Aan het verslapen',
]

const shuttingOffMessages = [
  'Bij aan het komen',
  'Aan het skaten',
  'RuneScape aan het spelen',
  'Goudgele rakker aan het drinken',
]

interface OnlineStatusProps {
  onStatusChange?: (status: StatusType) => void
}

function OnlineStatus({ onStatusChange }: OnlineStatusProps = {}) {

  // State to store the current status and selected random message
  const [currentStatus, setCurrentStatus] = useState<TimeBasedStatus | null>(null)

  const getTimeBasedStatus = (): TimeBasedStatus => {
    // Check for developer time override
    const timeOverride = localStorage.getItem('dev-time-override')
    let currentTime: Date
    
    if (timeOverride) {
      currentTime = new Date(timeOverride)
    } else {
      const now = new Date()
      // Get time in Amsterdam timezone (Europe/Amsterdam)
      currentTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Amsterdam"}))
    }
    
    const hours = currentTime.getHours()
    
    if (hours >= 8 && hours < 17) {
      // 08:00 - 17:00: Online (green)
      return {
        type: 'online',
        text: 'Online'
      }
    } else if (hours >= 17 && hours < 23) {
      // 17:00 - 23:00: Shutting off (orange)
      return {
        type: 'shutting-off',
        text: '' // Will be set below
      }
    } else if (hours >= 23 || hours < 6) {
      // 23:00 - 06:00: Offline (red)
      return {
        type: 'offline',
        text: 'Offline'
      }
    } else {
      // 06:00 - 08:00: Waking up (orange)
      return {
        type: 'waking-up',
        text: '' // Will be set below
      }
    }
  }

  // Update status when component mounts or time period changes
  useEffect(() => {
    const updateStatus = () => {
      const newStatus = getTimeBasedStatus()
      
      // Only update if the status type changed or if we don't have a current status
      if (!currentStatus || currentStatus.type !== newStatus.type) {
        // Set random message for time periods that need it
        if (newStatus.type === 'shutting-off') {
          newStatus.text = shuttingOffMessages[Math.floor(Math.random() * shuttingOffMessages.length)]
        } else if (newStatus.type === 'waking-up') {
          newStatus.text = wakingUpMessages[Math.floor(Math.random() * wakingUpMessages.length)]
        }
        
        setCurrentStatus(newStatus)
        
        // Notify parent component of status change
        if (onStatusChange) {
          onStatusChange(newStatus.type)
        }
      }
    }

    // Update immediately
    updateStatus()

    // Check for time changes every minute
    const interval = setInterval(updateStatus, 60000)

    return () => clearInterval(interval)
  }, [currentStatus, onStatusChange]) // Include currentStatus and onStatusChange in dependency array

  // Use current status or fallback
  const { type, text } = currentStatus || { type: 'online' as StatusType, text: 'Online' }

  return (
    <div className="online-status">
      <div className={`status-indicator ${type}`}></div>
      <span className="status-text">{text}</span>
    </div>
  )
}

export default OnlineStatus
